import { PropertyList, TuitionRatesHistoryPanel, TuitionRatesModal } from '@app/components'
import { formatCurrency, useTuitionRates, useTuitionRatesHistory } from '@app/lib'
import { type SugarPublishedTuitionRate } from '@app/types'
import { ActionIcon, Alert, Badge, Group, LoadingOverlay, Paper, Stack, Text, Title, Tooltip } from '@mantine/core'
import { IconAlertCircle, IconLock } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { type FC } from 'react'

export interface TuitionRatesTabProps {
  modalOpened: boolean
  onModalClose: () => void
}

const rateItems = (record: SugarPublishedTuitionRate) => [
  {
    label: 'Maximum Monthly Tuition Rate (4-Year-Old Cohort)',
    value: formatCurrency(record.fourYearOld.maxMonthlyRate)
  },
  {
    label: 'Maximum Monthly Tuition Rate (3-Year-Old Cohort)',
    value:
      record.ratesDifferThreeYr && record.threeYearOld ? (
        formatCurrency(record.threeYearOld.maxMonthlyRate)
      ) : (
        <Text
          color='dimmed'
          size='sm'
        >
          Same as 4-Year-Old
        </Text>
      )
  },
  {
    label: 'DPP Scholarship Program',
    value: (
      <Group spacing='xs'>
        {record.scholarshipOptIn === null ? (
          <Text
            color='dimmed'
            size='sm'
          >
            Not yet selected
          </Text>
        ) : (
          <Text size='sm'>{record.scholarshipOptIn ? 'Opted In' : 'Opted Out'}</Text>
        )}
        {record.scholarshipOptInLocked && (
          // Mantine 6's Tooltip sets no aria attributes at all and defaults to
          // hover-only, so a keyboard/screen-reader user needs a focusable
          // trigger, focus in Tooltip's events, and an aria-label carrying the
          // same text as the tooltip.
          <Tooltip
            events={{ focus: true, hover: true, touch: true }}
            label='Contact DPP at info@dpp.org or 303-595-4377 to change this selection.'
          >
            <ActionIcon
              aria-label='Locked. Contact DPP at info@dpp.org or 303-595-4377 to change this selection.'
              color='gray'
              size='sm'
              tabIndex={0}
              variant='light'
            >
              <IconLock size={14} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    )
  },
  {
    label: 'Effective Date',
    value: dayjs(record.effectiveDate).format('MMM D, YYYY')
  }
]

export const TuitionRatesTab: FC<TuitionRatesTabProps> = ({ modalOpened, onModalClose }) => {
  const { data: openRecord, isFetching } = useTuitionRates()
  const { data: history } = useTuitionRatesHistory()

  const historyList = history ?? []

  // Not shown when the year is locked: the banner points at radios the modal
  // disables in that state, so it would send the provider to a dead end. A
  // locked row can legitimately carry a null choice (staff can clear the value
  // and leave the lock date stamped) -- contacting DPP is the only route then,
  // which the modal's own lock notice already spells out.
  const needsScholarshipChoice =
    !!openRecord && openRecord.scholarshipOptIn === null && !openRecord.scholarshipOptInLocked
  const noRecord = !openRecord && historyList.length === 0
  const expiredOrIncomplete = !openRecord && historyList.length > 0

  const banner = needsScholarshipChoice ? (
    <Alert
      color='yellow'
      icon={<IconAlertCircle size={18} />}
      mb='lg'
      radius='lg'
      title='Choose DPP Scholarship participation'
    >
      Please open Enter New Rates and select Opt In or Opt Out for the DPP Scholarship Program.
    </Alert>
  ) : noRecord ? (
    <Alert
      color='yellow'
      icon={<IconAlertCircle size={18} />}
      mb='lg'
      radius='lg'
      title='No tuition rates yet'
    >
      No tuition rates yet — submit your rates to continue to Attendance.
    </Alert>
  ) : expiredOrIncomplete ? (
    <Alert
      color='yellow'
      icon={<IconAlertCircle size={18} />}
      mb='lg'
      radius='lg'
      title='Tuition rates incomplete'
    >
      Tuition rates incomplete — your prior rates expired or no current record is on file.
    </Alert>
  ) : null

  return (
    <>
      {banner}
      <Paper
        p='lg'
        pos='relative'
      >
        <LoadingOverlay
          radius='lg'
          visible={isFetching}
        />
        {openRecord ? (
          <PropertyList items={rateItems(openRecord)} />
        ) : (
          <Alert
            color='yellow'
            icon={<IconAlertCircle size={18} />}
            radius='lg'
            title='No current tuition rates on file'
          >
            <Text size='sm'>
              No current tuition rates on file. Click <strong>Enter New Rates</strong> above to submit them.
            </Text>
          </Alert>
        )}
      </Paper>
      {openRecord?.upcoming && (
        <Paper
          mt='lg'
          p='lg'
        >
          <Stack>
            <Group position='apart'>
              <Title order={5}>Scheduled Rate Change</Title>
              <Badge
                color='blue'
                variant='light'
              >
                Upcoming
              </Badge>
            </Group>
            <Text
              color='dimmed'
              size='sm'
            >
              These rates replace the ones above on {dayjs(openRecord.upcoming.effectiveDate).format('MMM D, YYYY')}.
            </Text>
            <PropertyList items={rateItems(openRecord.upcoming)} />
          </Stack>
        </Paper>
      )}
      <TuitionRatesHistoryPanel
        currentRecordId={openRecord?.id}
        history={historyList}
      />
      <TuitionRatesModal
        onClose={onModalClose}
        opened={modalOpened}
        openRecord={openRecord ?? null}
      />
    </>
  )
}
